// Notifications about a shopper's orders outside the page: push notifications through the service worker
// (public/sw.js), which reach the installed store app or a browser even when the store is closed; and plain browser
// notifications as a fallback where push is not available (development builds, or a browser without the Push API).
// The admin panel has the same helpers (apps/admin/src/utils/pushNotifications.js).
import { getPushKey, savePushSubscription } from "../services/alerts";

export const notificationsSupported = () => typeof window !== "undefined" && "Notification" in window;
export const pushSupported = () => notificationsSupported() && "serviceWorker" in navigator && "PushManager" in window && process.env.NODE_ENV === "production";
export const notificationPermission = () => (notificationsSupported() ? Notification.permission : "denied");

// The Push API wants the server's public key as bytes
function keyBytes(base64) {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

// This browser's push subscription, if it has one
export async function currentPushSubscription() {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

// Subscribes this browser and tells the server (again, in case another user signed in on it). Returns true when push
// notifications are on.
export async function enablePush() {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(await getPushKey()) });
  }
  await savePushSubscription(subscription.toJSON());
  return true;
}

// Asks for permission (must follow a click), then turns push on where possible. Returns the permission.
export async function turnOnNotifications() {
  if (!notificationsSupported()) return "denied";
  const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  if (permission === "granted") await enablePush().catch(() => false);
  return permission;
}
